// "Is Request App on the App Store yet?" — asks the server once per page load
// (GET /api/app-store, backed by Apple's public lookup API with a 15-minute
// cache). Every "Download the app" surface on the WEB keys off this, so they
// appear by themselves the moment Apple publishes the listing — no deploy,
// no human. Native app builds never show these surfaces (they ARE the app).

import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import api from "../api";

export interface AppStoreStatus {
  live: boolean;
  url: string | null;
}

const NOT_LIVE: AppStoreStatus = { live: false, url: null };
const KEY = "rq_appstore_status";
const TTL_MS = 15 * 60 * 1000;

let memo: { at: number; v: AppStoreStatus } | null = null;
let inflight: Promise<AppStoreStatus> | null = null;

function readSession(): { at: number; v: AppStoreStatus } | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p.at === "number" && p.v ? p : null;
  } catch {
    return null;
  }
}

export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export async function getAppStoreStatus(): Promise<AppStoreStatus> {
  if (isNativeApp()) return NOT_LIVE; // the app never advertises itself
  const now = Date.now();
  if (memo && now - memo.at < TTL_MS) return memo.v;
  const s = readSession();
  if (s && now - s.at < TTL_MS) {
    memo = s;
    return s.v;
  }
  if (!inflight) {
    inflight = api
      .get("/app-store")
      .then((r) => {
        const v: AppStoreStatus = { live: !!r.data?.live, url: r.data?.live && r.data?.url ? String(r.data.url) : null };
        memo = { at: Date.now(), v };
        try {
          sessionStorage.setItem(KEY, JSON.stringify(memo));
        } catch {}
        return v;
      })
      .catch(() => NOT_LIVE)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

// React hook: starts as "not live" (or the cached answer) and settles once the
// server replies. Components render nothing until live — so on the day the
// listing appears, the surfaces simply start showing up.
export function useAppStoreStatus(): AppStoreStatus {
  const [status, setStatus] = useState<AppStoreStatus>(() => memo?.v ?? readSession()?.v ?? NOT_LIVE);
  useEffect(() => {
    let on = true;
    getAppStoreStatus().then((v) => {
      if (on) setStatus(v);
    });
    return () => {
      on = false;
    };
  }, []);
  return status;
}
